import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How quickly will I receive a response?',
    answer: 'We guarantee a response within 2 hours during business hours (Mon-Fri, 9AM-6PM EST). For urgent inquiries marked as high priority, we aim to respond within 30 minutes. After-hours inquiries are addressed first thing the next business day.'
  },
  {
    id: '2',
    question: 'What information should I prepare for the consultation?',
    answer: 'For the most productive consultation, prepare: current technology stack overview, key business challenges, project timeline expectations, approximate budget range, and any specific technical requirements. However, if you\'re in early planning stages, we can help you define these parameters.'
  },
  {
    id: '3',
    question: 'Is the initial consultation free?',
    answer: 'Yes, all initial consultations are complimentary. This includes quick questions (15 min), strategic consultations (45 min), and technical demos (60 min). We believe in demonstrating value before asking for commitment.'
  },
  {
    id: '4',
    question: 'Can I schedule a consultation outside business hours?',
    answer: 'Absolutely. We offer flexible scheduling including early morning, evening, and weekend slots for clients in different time zones or with scheduling constraints. Select your preferred time during the booking process.'
  },
  {
    id: '5',
    question: 'What happens after I submit this form?',
    answer: 'You\'ll receive an immediate confirmation email, followed by a personalized response from your assigned team member within 2 hours. They\'ll either answer your question directly or schedule a consultation based on your selected engagement type.'
  },
  {
    id: '6',
    question: 'Do you sign NDAs before discussing projects?',
    answer: 'Yes, we\'re happy to sign mutual NDAs before any detailed project discussions. We understand the sensitive nature of digital transformation initiatives and maintain strict confidentiality for all client communications.'
  }
];

interface FAQSectionProps {
  expandedId: string | null;
  onToggle: (id: string) => void;
}

const FAQSection = ({ expandedId, onToggle }: FAQSectionProps) => {
  return (
    <div data-component-id="src/app/contact/components/FAQSection.tsx:50:4" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="50" data-component-end-line="85" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
      {faqs.map((faq) => (
        <div data-component-id="src/app/contact/components/FAQSection.tsx:52:8" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="52" data-component-end-line="83" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-xl%20overflow-hidden%20transition-all%20duration-300%20hover%3Ashadow-subtle%22%7D"
          key={faq.id}
          className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-subtle"
        >
          <button data-component-id="src/app/contact/components/FAQSection.tsx:56:10" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="56" data-component-end-line="70" data-component-file="FAQSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-4%20flex%20items-center%20justify-between%20text-left%20hover%3Abg-muted%2F50%20transition-colors%20duration-200%22%7D"
            onClick={() => onToggle(faq.id)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-200"
          >
            <span data-component-id="src/app/contact/components/FAQSection.tsx:60:12" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="60" data-component-end-line="62" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-base%20font-heading%20font-semibold%20text-foreground%20pr-4%22%7D" className="text-base font-heading font-semibold text-foreground pr-4">
              {faq.question}
            </span>
            <Icon data-component-id="src/app/contact/components/FAQSection.tsx:63:12" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="63" data-component-end-line="69" data-component-file="FAQSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
              name="ChevronDownIcon"
              size={20}
              className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                expandedId === faq.id ? 'rotate-180' : ''
              }`}
            />
          </button>
          
          <div data-component-id="src/app/contact/components/FAQSection.tsx:72:10" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="72" data-component-end-line="82" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={`overflow-hidden transition-all duration-300 ${
              expandedId === faq.id ? 'max-h-96' : 'max-h-0'
            }`}
          >
            <div data-component-id="src/app/contact/components/FAQSection.tsx:77:12" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="77" data-component-end-line="81" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20pb-4%22%7D" className="px-6 pb-4">
              <p data-component-id="src/app/contact/components/FAQSection.tsx:78:14" data-component-path="src/app/contact/components/FAQSection.tsx" data-component-line="78" data-component-end-line="80" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20leading-relaxed%22%7D" className="text-sm text-muted-foreground leading-relaxed">
                {faq.answer}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FAQSection;